/*
 * Copyright (c) 2023 Bluemate B.V.
 */

import {ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';

import React, {useEffect, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';

import Connection from '../../../models/connectionModel';

import {getMaintenanceMode} from '../../../util/maintenanceUtil';

import {toggleMaintenanceMode} from '../../../util/requestSender';

import AdminConnections from './adminConnections';

const PING_TIMEOUT = 300000; // Every 5 minutes

export default function AdminConnectionsController() {
    const {t} = useTranslation();
    const pingTimeout = useRef<undefined | number>(undefined);
    const [connections, setConnections] = useState<Array<Connection>>([]);
    const [loading, setLoading] = useState(false);

    function onToggleMaintenance(value: boolean) {
        const currentMode = getMaintenanceMode();
        const action = value
            ? t('admin.connections.enable', 'inschakelen')
            : t('admin.connections.disable', 'uitschakelen');
        showDialog(
            `${t('admin.connections.toggleConfirmPrefix', 'Weet je zeker dat je de onderhoudsmodus wilt')} ${action}?`,
            t('admin.connections.confirmTitle', 'Bevestig'),
            [
                {label: t('admin.connections.no', 'Nee'), primary: !currentMode},
                {
                    label: t('admin.connections.yes', 'Ja'),
                    onClick: onConfirmToggleMaintenance(value),
                    primary: currentMode
                }
            ]
        );
    }

    function onConfirmToggleMaintenance(value: boolean) {
        return function () {
            setLoading(true);
            toggleMaintenanceMode({
                data: {toggled: value},
                callback: (data: ResponseData) => {
                    setLoading(false);
                    const action = value
                        ? t('admin.connections.enabled', 'ingeschakeld')
                        : t('admin.connections.disabled', 'uitgeschakeld');
                    if (data.success) {
                        showSnackbar(
                            `${t('admin.connections.maintenanceModeIs', 'Onderhoud modus is')} ${action}, ${t('admin.connections.pageReloading', 'de pagina wordt opnieuw geladen')}`
                        );
                        window.setTimeout(() => {
                            window.location.reload();
                        }, 2000);
                    } else
                        showDialog(
                            data.message ??
                                t(
                                    'admin.connections.toggleFailedMsg',
                                    'Het wijzigen van de onderhoudsmodus is niet gelukt.'
                                )
                        );
                }
            });
        };
    }

    function getConnections() {
        Connection.getAll((connections: Connection[], success: boolean) => {
            if (success) setConnections(connections);
        });
        pingTimeout.current = window.setTimeout(getConnections, PING_TIMEOUT);
    }

    useEffect(() => {
        getConnections();
        return function () {
            window.clearTimeout(pingTimeout.current);
            pingTimeout.current = undefined;
        };
    }, []);

    return <AdminConnections onToggleMaintenance={onToggleMaintenance} connections={connections} loading={loading} />;
}
